import clsx from 'clsx';
import { Fragment, type ReactNode } from 'react';
import Btn from '../actions/Btn';
import { IconCaret } from '../icons';

export const CALENDAR_DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;

export type CalendarDayMeta = {
    dateKey: string;
    dayNumber: number;
    isToday: boolean;
};

export type CalendarCell =
    | { kind: 'pad'; key: string }
    | ({ kind: 'day'; key: string } & CalendarDayMeta);

/** Format a Date as YYYY-MM-DD in local time. */
export function toDateKey(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function formatDateLabel(dateKey: string): string {
    const [y, m, d] = dateKey.split('-').map(Number);
    if (!y || !m || !d) return dateKey;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
    });
}

export function formatMonthTitle(year: number, monthIndex: number): string {
    return new Date(year, monthIndex, 1).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
    });
}

export function buildMonthCells(
    year: number,
    monthIndex: number,
    todayKey: string = toDateKey(new Date()),
): CalendarCell[] {
    const first = new Date(year, monthIndex, 1);
    const daysInMonth = new Date(year, monthIndex + 1, 0).getDate();
    const startPad = first.getDay();
    const cells: CalendarCell[] = [];

    for (let i = 0; i < startPad; i += 1) {
        cells.push({ kind: 'pad', key: `pad-${year}-${monthIndex}-${i}` });
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
        const dateKey = toDateKey(new Date(year, monthIndex, day));
        cells.push({
            kind: 'day',
            key: dateKey,
            dateKey,
            dayNumber: day,
            isToday: dateKey === todayKey,
        });
    }
    return cells;
}

export function shiftMonth(
    year: number,
    monthIndex: number,
    delta: number,
): { year: number; month: number } {
    const date = new Date(year, monthIndex + delta, 1);
    return { year: date.getFullYear(), month: date.getMonth() };
}

export interface CalendarProps {
    /** Calendar year (full year, e.g. 2026). */
    year: number;
    /** 0-indexed month (0 = January). */
    month: number;
    /** Called when the user navigates to a different month. */
    onMonthChange: (year: number, month: number) => void;
    /**
     * When provided, replaces the default day cell entirely (including day number).
     * Use this for drag-and-drop or other interactive day surfaces.
     */
    renderDay?: (day: CalendarDayMeta) => ReactNode;
    className?: string;
    ariaLabel?: string;
    todayKey?: string;
}

export function Calendar({
    year,
    month,
    onMonthChange,
    renderDay,
    className,
    ariaLabel = 'Calendar',
    todayKey,
}: CalendarProps) {
    const resolvedTodayKey = todayKey ?? toDateKey(new Date());
    const cells = buildMonthCells(year, month, resolvedTodayKey);

    const goPrev = () => {
        const next = shiftMonth(year, month, -1);
        onMonthChange(next.year, next.month);
    };

    const goNext = () => {
        const next = shiftMonth(year, month, 1);
        onMonthChange(next.year, next.month);
    };

    return (
        <section className={clsx('calendar', className)} aria-label={ariaLabel}>
            <div className="calendar-header">
                <Btn
                    variant="icon"
                    ariaLabel="Previous month"
                    buttonIcon={<IconCaret iconClasses="calendar-caret-prev" />}
                    buttonOnClick={goPrev}
                    buttonClasses="calendar-month-nav"
                />
                <h3 className="calendar-month-title">{formatMonthTitle(year, month)}</h3>
                <Btn
                    variant="icon"
                    ariaLabel="Next month"
                    buttonIcon={<IconCaret iconClasses="calendar-caret-next" />}
                    buttonOnClick={goNext}
                    buttonClasses="calendar-month-nav"
                />
            </div>

            <div className="calendar-weekdays" aria-hidden="true">
                {CALENDAR_DAY_LABELS.map((label) => (
                    <span key={label} className="calendar-weekday">
                        {label}
                    </span>
                ))}
            </div>

            <div className="calendar-grid">
                {cells.map((cell) => {
                    if (cell.kind === 'pad') {
                        return (
                            <div
                                key={cell.key}
                                className="calendar-day calendar-day--pad"
                                aria-hidden="true"
                            />
                        );
                    }

                    if (renderDay) {
                        return <Fragment key={cell.key}>{renderDay(cell)}</Fragment>;
                    }

                    return (
                        <div
                            key={cell.key}
                            className={clsx('calendar-day', cell.isToday && 'is-today')}
                        >
                            <span className="calendar-day-num">{cell.dayNumber}</span>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

export default Calendar;
